import { useState, useEffect } from 'react';
import { PhoneOutlineIcon } from '@applyboard/ui-icons';
import {
  GridAlpha,
  Flex,
  Heading,
  Text,
  Button,
  TextInput,
  PhoneInput,
  Select,
  useToasts,
  Checkbox,
} from '@applyboard/crystal-ui';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { formatDateToLocal } from '~/utils';

import { Card } from '../Card';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { useUpdatePersonalInformation } from '../../../data/useUpdatePersonalInformation';
import { validateContactInformation } from './ContactInformation.validation';

interface ContactInfo {
  phoneNumber: string;
  alternatePhoneNumber?: string;
  email: string;
}

interface EmergencyContactInfo {
  name: string;
  relationship: string;
  phoneNumber: string;
  email: string;
}

interface EditedData {
  phoneNumber: string;
  alternatePhoneNumber: string;
  email: string;
  emergencyContact: EmergencyContactInfo;
}

// Helper function to create the edited data structure from props
function getEditedDataFromProps(
  contactInformation: ContactInfo,
  emergencyContact?: { name?: string; relationship?: { code?: string }; phoneNumber?: string; email?: string } | null
): EditedData {
  return {
    phoneNumber: contactInformation.phoneNumber,
    alternatePhoneNumber: contactInformation.alternatePhoneNumber || '',
    email: contactInformation.email,
    emergencyContact: {
      name: emergencyContact?.name || '',
      relationship: emergencyContact?.relationship?.code || '',
      phoneNumber: emergencyContact?.phoneNumber || '',
      email: emergencyContact?.email || '',
    },
  };
}

export function ContactInformation() {
  const {
    application: {
      id: applicationId,
      submittedAt,
      applicant: { contactInformation, emergencyContact },
    },
    relationshipOptions,
    entityHistoryMap,
  } = useAppProcessing();

  const updateMutation = useUpdatePersonalInformation();
  const toast = useToasts();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(() => getEditedDataFromProps(contactInformation, emergencyContact));
  const [removeEmergencyContact, setRemoveEmergencyContact] = useState(false);

  // Sync local state with props when they change (e.g., after successful mutation)
  // Only update when not editing to avoid overwriting user's in-progress changes
  useEffect(() => {
    if (!isEditing) {
      setEditedData(getEditedDataFromProps(contactInformation, emergencyContact));
    }
  }, [contactInformation, emergencyContact, isEditing]);

  const handleFieldChange = (field: string, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmergencyContactChange = (field: string, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      emergencyContact: { ...prev.emergencyContact, [field]: value },
    }));
  };

  const handleSave = async () => {
    // If user wants to remove emergency contact, skip validation
    if (!removeEmergencyContact) {
      // Validate contact information
      const validationResult = validateContactInformation(editedData);

      if (!validationResult.isValid) {
        toast.negative(new Error(validationResult.errorMessage!));
        return;
      }
    }

    try {
      // If user wants to remove emergency contact, don't include it
      // Otherwise check if any emergency contact field has a value
      const hasEmergencyContact =
        !removeEmergencyContact &&
        (editedData.emergencyContact.name ||
          editedData.emergencyContact.relationship ||
          editedData.emergencyContact.phoneNumber ||
          editedData.emergencyContact.email);

      const payload = {
        id: applicationId,
        personalInformation: {
          contactInformation: {
            phoneNumber: editedData.phoneNumber,
            alternatePhoneNumber: editedData.alternatePhoneNumber || null,
            email: editedData.email,
          },
          ...(removeEmergencyContact
            ? {
                emergencyContact: null,
              }
            : hasEmergencyContact && {
                emergencyContact: {
                  name: editedData.emergencyContact.name || null,
                  relationship: editedData.emergencyContact.relationship || null,
                  phoneNumber: editedData.emergencyContact.phoneNumber || null,
                  email: editedData.emergencyContact.email || null,
                },
              }),
        },
      };

      await updateMutation.mutateAsync(payload);
      toast.positive('Contact information updated successfully');
      setRemoveEmergencyContact(false);
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update contact information';
      toast.negative(new Error(errorMessage));
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setEditedData(getEditedDataFromProps(contactInformation, emergencyContact));
    setRemoveEmergencyContact(false);
    setIsEditing(false);
  };

  const contactEntry = entityHistoryMap?.contactInformation;
  const isContactUpdatedAfterSubmission =
    contactEntry?.updatedAt && submittedAt && new Date(contactEntry.updatedAt) > new Date(submittedAt);
  const formattedUpdatedAt = isContactUpdatedAfterSubmission
    ? formatDateToLocal(new Date(contactEntry.updatedAt))
    : undefined;

  return (
    <Card id="contactInformation" icon={PhoneOutlineIcon} title="Contact Information">
      {formattedUpdatedAt && (
        <Text variant="bodyS" contrast="mid">
          Updated on {formattedUpdatedAt}
          {contactEntry?.updatedByRole && <> by {contactEntry.updatedByRole}</>}
        </Text>
      )}
      {!isEditing && (
        <Flex gap={2} justify="end">
          <Button size="sm" variant="outlined" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        </Flex>
      )}
      <Flex justify="start" direction="column">
        {isEditing ? (
          <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(2, 1fr)">
            <PhoneInput
              label="PHONE NUMBER"
              value={editedData.phoneNumber}
              onChange={(value: string) => handleFieldChange('phoneNumber', value)}
              required
            />
            <PhoneInput
              label="ALTERNATIVE PHONE NUMBER"
              value={editedData.alternatePhoneNumber}
              onChange={(value: string) => handleFieldChange('alternatePhoneNumber', value)}
            />
            <TextInput
              label="EMAIL"
              value={editedData.email}
              onChange={(value: string) => handleFieldChange('email', value)}
              type="email"
              required
            />
          </GridAlpha>
        ) : (
          <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(2, 1fr)">
            <LabelValue label="PHONE NUMBER" value={contactInformation.phoneNumber} />
            <LabelValue label="ALTERNATIVE PHONE NUMBER" value={contactInformation.alternatePhoneNumber || '--'} />
            <LabelValue label="EMAIL" value={contactInformation.email} />
          </GridAlpha>
        )}

        <Flex direction="column" role="region" aria-describedby="Emergency Contact" gap={emergencyContact ? 8 : 1}>
          <Heading level={4} variant="titleS">
            Emergency Contact
          </Heading>
          {isEditing && emergencyContact && (
            <Checkbox
              label="Would you like to remove the emergency contact?"
              value={removeEmergencyContact}
              onChange={setRemoveEmergencyContact}
            />
          )}
          {isEditing ? (
            <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(2, 1fr)">
              <TextInput
                label="NAME"
                value={editedData.emergencyContact.name}
                onChange={(value: string) => handleEmergencyContactChange('name', value)}
                required={!removeEmergencyContact}
                disabled={removeEmergencyContact}
              />
              <Select
                label="RELATIONSHIP"
                value={editedData.emergencyContact.relationship}
                onChange={(value: string) => handleEmergencyContactChange('relationship', value)}
                appearance="styled"
                required={!removeEmergencyContact}
                disabled={removeEmergencyContact}
              >
                {relationshipOptions.map((option) => (
                  <Select.Option key={option.code} label={option.name} value={option.code} />
                ))}
              </Select>
              <GridAlpha.Item rowStart={2} rowEnd={2}>
                <PhoneInput
                  label="PHONE NUMBER"
                  value={editedData.emergencyContact.phoneNumber}
                  onChange={(value: string) => handleEmergencyContactChange('phoneNumber', value)}
                  required={!removeEmergencyContact}
                  disabled={removeEmergencyContact}
                />
              </GridAlpha.Item>
              <GridAlpha.Item rowStart={3} rowEnd={3}>
                <TextInput
                  label="EMAIL"
                  value={editedData.emergencyContact.email}
                  onChange={(value: string) => handleEmergencyContactChange('email', value)}
                  required={!removeEmergencyContact}
                  disabled={removeEmergencyContact}
                />
              </GridAlpha.Item>
            </GridAlpha>
          ) : emergencyContact ? (
            <GridAlpha rows="repeat(3, auto)" columnGap={2} rowGap={6} columns="repeat(2, 1fr)">
              <LabelValue label="NAME" value={emergencyContact.name} />
              <LabelValue label="RELATIONSHIP" value={emergencyContact.relationship.name} />
              <GridAlpha.Item rowStart={2} rowEnd={2}>
                <LabelValue label="PHONE NUMBER" value={emergencyContact.phoneNumber} />
              </GridAlpha.Item>
              <GridAlpha.Item rowStart={3} rowEnd={3}>
                <LabelValue label="EMAIL" value={emergencyContact.email} />
              </GridAlpha.Item>
            </GridAlpha>
          ) : (
            <Text variant="bodyM" contrast="mid">
              Not provided
            </Text>
          )}
        </Flex>
        {isEditing && (
          <Flex gap={2} justify="end">
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
